import { SchedulerHelper } from "@/Appointments/Helpers/SchedulerHelper";
import { CalendarAppointmentDisplay } from "@/Appointments/Models/Calendar/CalendarAppointmentDisplay";
import { ICalendarDisplaySettingsModel } from "@/Appointments/Models/UI/ICalendarDisplaySettingsModel";
import { ICalendarModel } from "@/Appointments/Models/UI/ICalendarModel";
import { QuestionnaireLinkConfiguration } from "@/Common/Components/Questionnaire/QuestionnaireLinkConfiguration";
import { Guid } from "@/Common/Models/Guid";
import { QuestionnaireStatusInfo } from "@/Common/Models/Questionnaires/QuestionnaireStatusInfo";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";

import "./KendoSchedulerControl.css";

/// <reference path="../../../../lib/kendo/kendo.all.d.ts" />

interface IKendoSchedulerProps {
    ControlId: string;
    CalendarModel: ICalendarModel;
    CalendarDisplaySettingsModel: ICalendarDisplaySettingsModel;
    openDeletionPopup: (deletionEventInfo?: kendo.data.SchedulerEvent & CalendarAppointmentDisplay) => void;
}

class KendoScheduler extends React.Component<IKendoSchedulerProps, never> {
    private resizeDebounceTimer: number;
    private scrollTimerReference: number;
    private readonly weekdays = 7;

    private prevBtn: JQuery; // references
    private nextBtn: JQuery; // references

    private scrollPosition: number;

    constructor(props: IKendoSchedulerProps) {
        super(props);
        window.SchedulerHelper = SchedulerHelper;
        window.QuestionnaireLinkConfiguration = QuestionnaireLinkConfiguration;
    }

    public render(): JSX.Element {
        const isLast = this.props.CalendarModel.get("CurrentPageEmployeeIndex") === (this.props.CalendarModel.get("TotalEmployeePages") - 1);
        const isFirst = this.props.CalendarModel.get("CurrentPageEmployeeIndex") === 0;

        const dataText0 = "#= SchedulerHelper.GetCsvValueByIndex(data.text, 0) #";
        const dataText1 = "#= SchedulerHelper.GetCsvValueByIndex(data.text, 1) #";
        const dataText2 = "#= SchedulerHelper.GetCsvValueByIndex(data.text, 2) #";
        const dataText3 = "#= SchedulerHelper.GetCsvValueByIndex(data.text, 3) #";
        const dataText4 = "#= SchedulerHelper.GetCsvValueByIndex(data.text, 4) #";
        const dataText5 = "#= SchedulerHelper.GetCsvValueByIndex(data.text, 5) #";
        const dataText6 = "#= SchedulerHelper.GetCsvValueByIndex(data.text, 6) #";

        const scheduleInfo = this.props.CalendarModel.get("IsCalendarDayView") ? `${dataText3} ${dataText4} | ${dataText5} ${dataText6}` : (this.props.CalendarModel.get("IsCalendarWeekView") || this.props.CalendarModel.get("IsCalendarMonthView")) ? `${dataText3} ${dataText4}` : "";
        const titleInfo = `${dataText0} - ${dataText1}\n${dataText2}\n${scheduleInfo}`;

        return (
            <div className="myle-scheduler inherit-height" translate="no">
                <div className="schedule-wrapper" id={this.props.ControlId} />
                <div style={{ display: "none" }}>
                    <div id="groupHeaderTemplate">
                        <div className="cal-header-labels" data-role="doc" title={titleInfo}>
                            {!isLast && (
                                <button
                                    className="btn hide cal-nav last btn-sm btn-default pull-right"
                                >
                                    <span className="glyphicon glyphicon-chevron-right" />
                                </button>
                            )}
                            {!isFirst && (
                                <button
                                    className="btn hide cal-nav first btn-sm btn-default pull-left"
                                >
                                    <span className="glyphicon glyphicon-chevron-left" />
                                </button>
                            )}
                            <div>{dataText0} - {dataText1}</div>
                            <div className="sub">{dataText2}</div>
                            {this.props.CalendarModel.get("IsCalendarDayView") &&
                                <div className="sub">{dataText3} {dataText4} {dataText5} {dataText6}</div>
                            }
                            {(this.props.CalendarModel.get("IsCalendarWeekView") || this.props.CalendarModel.get("IsCalendarMonthView")) &&
                                <div className="sub">{dataText3} {dataText4} </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private onWindowResize(): void {
        let self = this;

        if (this.resizeDebounceTimer !== -1) {
            clearTimeout(this.resizeDebounceTimer);
            this.resizeDebounceTimer = -1;
        }

        this.resizeDebounceTimer = window.setTimeout(function() {
            try {
                if (self.props == undefined) {
                    return;
                }
                const selector = $("#" + this.props.ControlId);
                const component = selector.data("kendoScheduler");
                if (component != null) {
                    component.resize(true);
                }
            }
            catch (err) {
                console.log("error occured!");
            }
        }, 200);
    }

    public shouldComponentUpdate(nextProps: IKendoSchedulerProps): boolean {
        return (
            this.props.CalendarModel.get("IsCalendarWeekView") !== nextProps.CalendarModel.get("IsCalendarWeekView")
            || this.props.CalendarModel.get("AppointmentsDisplay") !== nextProps.CalendarModel.get("AppointmentsDisplay")
            || this.props.CalendarModel.get("PredefinedAppointmentsDisplay") !== nextProps.CalendarModel.get("PredefinedAppointmentsDisplay")
            || this.props.CalendarModel.get("IsCalendarDayView") !== nextProps.CalendarModel.get("IsCalendarDayView")
            || this.props.CalendarModel.get("IsCalendarMonthView") !== nextProps.CalendarModel.get("IsCalendarMonthView")
            || this.props.CalendarModel.get("CalendarStartDate") !== nextProps.CalendarModel.get("CalendarStartDate")
            || this.props.CalendarModel.get("DisplayedEmployees") !== nextProps.CalendarModel.get("DisplayedEmployees")
            || this.props.CalendarModel.get("PredefinedRuleSets") !== nextProps.CalendarModel.get("PredefinedRuleSets")
        );
    }

    public componentDidUpdate(): void {
        this.destroyScheduler();
        this.displayScheduler();
    }

    public displayScheduler(): void {
        const self = this;

        const schedulerElement = $("#" + this.props.ControlId);
        const isDayView = this.props.CalendarModel.get("IsCalendarDayView");
        const isWeekView = this.props.CalendarModel.get("IsCalendarWeekView");
        const isMonthView = this.props.CalendarModel.get("IsCalendarMonthView");

        const displayedEmployeesCount = this.props.CalendarModel.get("DisplayedEmployees").count();
        const sortByScheduleSelectedEmployees = cacheStore.ClinicSettings.EnableDynamicRosterCalendarSorting && this.props.CalendarDisplaySettingsModel.get("SortByScheduleSelectedEmployees");
        const lastEmployeeIndexWithAppointment = this.props.CalendarModel.get("AllEmployees").findLastIndex(emp => (emp.AppointmentCount + emp.AvailabilityCount > 0) || emp.HasScheduledRuleset);
        const currentPageEmployeeIndex = this.props.CalendarModel.get("CurrentPageEmployeeIndex");

        // define vertical groupings ( employees displayed in calendar)
        const groupingKendoResource = {
            field: "EmployeeContactFacetId",
            name: "EmployeeGrouping",
            dataSource: this.getEmployeesAsKendoDataSource()
        };

        // event html template
        const template = this.generateAppointmentTemplate(this.props);

        // set appointment datasource
        let appointments: CalendarAppointmentDisplay[] = this.props.CalendarModel.get("AppointmentsDisplay")?.toArray() ?? [];
        const predefinedAppointments = this.props.CalendarModel.get("PredefinedAppointmentsDisplay")?.toArray() ?? [];
        const predefinedRuleSets = this.props.CalendarModel.get("PredefinedRuleSetsDisplay")?.toArray() ?? [];
        appointments = appointments.concat(predefinedAppointments);

        if ((isDayView || isWeekView) && predefinedRuleSets) {
            appointments = appointments.concat(predefinedRuleSets);
        }

        // set calendarview
        let calendarView = [];
        const isCalendarReadOnly = cacheStore.ClinicSettings.IsCalendarReadOnly;
        if (isDayView) calendarView = [{ type: "day", selected: true }];
        if (isWeekView) calendarView = [{ type: "week", selected: true }];
        if (isMonthView) calendarView = [{ type: "month", selected: true }];
        let calendarDate = this.props.CalendarModel.get("CalendarStartDate");

        const startHour = cacheStore.ClinicSettings.Enable24hSchedule ? "0" : "6";

        schedulerElement.kendoScheduler({
            autoBind: true,
            date: calendarDate, // The current date of the scheduler
            groupHeaderTemplate: $("#groupHeaderTemplate").html(),
            group: { resources: ["EmployeeGrouping"] }, // to set vertical time lines based on the "Doctors resource"
            eventTemplate: template,
            allDaySlot: false,
            views: calendarView,
            dataSource: new kendo.data.SchedulerDataSource({ data: appointments }),
            edit: this.onSchedulerGridClick.bind(this),
            navigate: e => this.onViewNavigate(e),
            editable: {
                move: false,
                resize: false,
                create: !isMonthView,
                update: !isMonthView,
                destroy: !isMonthView && !isCalendarReadOnly,
                confirmation: false
            },
            footer: false,
            selectable: true,
            majorTick: 60,
            minorTickCount: 60 / this.props.CalendarDisplaySettingsModel.get("TimeSlotLength"),
            startTime: new Date("2013/6/6 0" + startHour + ":00"),
            endTime: new Date("2013/6/7 00:00"),
            snap: false,
            mobile: false,
            timezone: "",
            resources: [groupingKendoResource],
            dataBinding: () => {
                // cleanup
                this.prevBtn?.off("click.cal");
                this.nextBtn?.off("click.cal");
                $(".k-link.k-event-delete").off("click");
            },
            dataBound: (e: kendo.ui.SchedulerDataBoundEvent) => {
                if (e.sender.data().length > 0) {
                    $("a.k-link.k-event-delete").each(function() {
                        const newDiv = $("<div>").on("click", event => {
                            event.preventDefault();
                            const uid = $(event.target).closest(".k-event").attr("data-uid");
                            const eventInfo: any = scheduler.occurrenceByUid(uid);
                            self.props.openDeletionPopup(eventInfo);
                            event.stopPropagation();
                        });

                        $(this).clone().removeAttr("href").appendTo(newDiv);
                        $(this).replaceWith(newDiv);
                    });

                    const verticalSplitterPosition = lastEmployeeIndexWithAppointment - currentPageEmployeeIndex;
                    const showVerticalSplitter = verticalSplitterPosition >= 0 && verticalSplitterPosition < displayedEmployeesCount - 1;
                    if (sortByScheduleSelectedEmployees && showVerticalSplitter) {
                        const headerEmpElements = document.querySelectorAll(`#${this.props.ControlId} div[class='k-scheduler-header-wrap'] th[class='k-slot-cell']`);
                        const headerWeekElements = document.querySelectorAll(`#${this.props.ControlId} div[class='k-scheduler-header-wrap'] tbody tr:nth-child(2) th`);
                        const bodyElements = document.querySelectorAll(`#${this.props.ControlId} div[class='k-scheduler-content'] td[role='gridcell']`);
                        const borderRightStyle = "4px solid #1E355C";

                        const setBorderStyle = (element: HTMLElement, index: number): void => {
                            const showRightBorder = isDayView
                                ? index % displayedEmployeesCount === verticalSplitterPosition
                                : (index + 1) % (displayedEmployeesCount * this.weekdays) === (lastEmployeeIndexWithAppointment + 1 - currentPageEmployeeIndex) * this.weekdays;

                            if (showRightBorder)
                                element.style.borderRight = borderRightStyle;
                        };

                        const headerElement = headerEmpElements[verticalSplitterPosition] as HTMLElement;
                        if (headerElement)
                            headerElement.style.borderRight = borderRightStyle;

                        headerWeekElements.forEach(setBorderStyle);
                        bodyElements.forEach(setBorderStyle);
                    }
                }

                const headers = schedulerElement.find('div[data-role="doc"]');
                if (headers.length) {
                    let pr = headers.first().find(".btn.first");
                    let nx = headers.last().find(".btn.last");
                    pr.removeClass("hide");
                    nx.removeClass("hide");

                    $(pr).on("click.cal", () => {
                        window.setTimeout(() => {
                            let operation = new LocalEvent(LocalOperations.Calendar.CalendarPage_Previous_Click);
                            LocalEventDispatcher.dispatch(operation);
                        }, 0);
                    });
                    $(nx).on("click.cal", () => {
                        window.setTimeout(() => {
                            let operation = new LocalEvent(LocalOperations.Calendar.CalendarPage_Next_Click);
                            LocalEventDispatcher.dispatch(operation);
                        }, 0);
                    });

                    this.prevBtn = pr;
                    this.nextBtn = nx;
                }

                // remove bullshit kendo spans
                $(".k-event-actions").filter(function() {
                    return this.children == null || this.children.length == 0;
                }).remove();

                if (isDayView) {
                    let tables = $(".k-scheduler-header-wrap .k-scheduler-table");
                    tables = tables.last();
                    let rows = tables.find("tr:last");
                    rows.each(function() {
                        $(this).children("th").hide();
                    });

                    let tables2 = $(".k-scheduler-times .k-scheduler-table");
                    tables2 = tables2.first();
                    tables2.hide();

                    let calendar = $("li.k-state-default.k-nav-current");
                    calendar.hide();

                    this.layoutRulesets();
                }
                if (isWeekView) {
                    const headerWeekdayElements = document.querySelectorAll(`#${this.props.ControlId} div[class='k-scheduler-header-wrap'] tbody tr:nth-child(2) th`);
                    headerWeekdayElements.forEach((element: HTMLElement, index: number): void => {
                        const date = calendarDate.addDays(index % this.weekdays);

                        element.style.cursor = "pointer";
                        element.onclick = () => this.onSchedulerNavigate(date);
                    });

                    this.layoutRulesets();
                }
            }
        });

        // next step
        const scheduler = schedulerElement.data("kendoScheduler");

        scheduler.wrapper.off("mousedown.calstate"); // cleanup
        scheduler.wrapper.on("mousedown.calstate", "td.k-state-selected", (e: JQueryEventObject) => {
            if (!isMonthView) {
                let target = $(e.currentTarget);
                let slot = scheduler.slotByElement(target[0]);
                let resource = scheduler.resourcesBySlot(slot);
                let id: Guid = resource.EmployeeContactFacetId;
                let startTime = slot.startDate;

                let operation = new LocalEvent(LocalOperations.Calendar.Appointment_NewAppointment_Dialog);
                operation.AddItem("employeeContactFacetId", id);
                operation.AddItem("startTime", startTime);
                LocalEventDispatcher.dispatch(operation);
            }
        });

        scheduler.wrapper.off("click.caltd"); // cleanup
        scheduler.wrapper.on("click.caltd", ".k-scheduler-table td", (e: JQueryEventObject) => {
            if (isMonthView) {
                let target = $(e.target);
                let slot = scheduler.slotByElement(target);
                let operation = new LocalEvent(LocalOperations.Calendar.Calendar_Monthview_DayClick);
                operation.AddItem("date", slot.startDate);
                LocalEventDispatcher.dispatch(operation);
            }
        });

        scheduler.wrapper.off("mousedown.div"); // cleanup
        scheduler.wrapper.on("mousedown.div", "div", (e: JQueryEventObject) => {
            e.preventDefault();
            e.stopPropagation();
        });

        scheduler.wrapper.off("click.caldiv"); // cleanup
        scheduler.wrapper.on("click.caldiv", "div.k-event", function(e: JQueryEventObject) {
            e.preventDefault();

            if (!isMonthView) {
                let uid = $(this).attr("data-uid");

                let eventInfo: any = scheduler.occurrenceByUid(uid);
                let appId = eventInfo.AppointmentId;
                let patientId = eventInfo.PatientId;
                let isPredefined = eventInfo.AppointmentStatusCode === "PREDEFINED";
                let isRuleset = eventInfo.AppointmentStatusCode === "PREDEFINEDRULESET";
                let questionnaireStatusInfo = eventInfo.QuestionnaireStatusInfo;
                let appointmentStartDate = eventInfo.start;
                let isSharedWithPortal = eventInfo.IsSharedWithPortal;
                let appointmentStatusLookupId = eventInfo.AppointmentStatusLookupId;

                if (!isRuleset) {
                    let clickedTarget = $(e.target);

                    if (eventInfo.PatientContactId && clickedTarget.attr("class") === self.redirectPatientClass) {
                        sessionStore.dispatch(LocalOperations.Calendar.RedirectToPatientFile, { patientContactId: eventInfo.PatientContactId });
                    }
                    else if (questionnaireStatusInfo
                        && QuestionnaireLinkConfiguration.shouldLinkBeActive(appointmentStartDate, appointmentStatusLookupId, questionnaireStatusInfo.Result !== null, patientId, isSharedWithPortal, questionnaireStatusInfo.QuestionnaireInstanceId)
                        && clickedTarget.attr("class") === self.questionnaireClickClass) {
                        self.handleOnQuestionnaireClick(questionnaireStatusInfo);
                    }
                    else {
                        let operation = new LocalEvent(LocalOperations.Calendar.Appointment_Show_Edit_Dialog);
                        operation.AddItem("isPredefined", isPredefined);
                        operation.AddItem("appointmentId", appId);
                        operation.AddItem("patientId", patientId);

                        LocalEventDispatcher.dispatch(operation);
                    }
                }
                else {
                    let id = eventInfo.id;
                    let operation = new LocalEvent(LocalOperations.Calendar.Appointment_Show_Ruleset_Edit);
                    operation.AddItem("rulesetId", id);
                    LocalEventDispatcher.dispatch(operation);
                }
            }
            else {
                if (e.button == 0) {
                    let target = $(e.currentTarget);
                    let slot = scheduler.slotByElement(target[0]);
                    let operation = new LocalEvent(LocalOperations.Calendar.Calendar_Monthview_DayClick);
                    operation.AddItem("date", slot.startDate);
                    LocalEventDispatcher.dispatch(operation);
                }
            }

            e.stopPropagation();
        });

        let reference = schedulerElement.find(".k-scheduler-content");// .data("kendoScheduler");
        let nativeReference = reference.get(0);
        let schedulerContentRef = $(nativeReference);
        let kendoDefaultBottomMargin = 23;

        // Initial render doesn't calcule height correctly on first load. Instead of rerendering everything to match the next render's height, adjust to correct height directly for first load.
        schedulerContentRef.css("height", `calc(100vh - ${schedulerContentRef.offset().top + kendoDefaultBottomMargin}px)`);

        let scrollPosition = this.props.CalendarModel.get("ScrollPosition");
        this.adjustScroll(scrollPosition, schedulerElement);

        reference.on("scroll", () => {
            this.scrollPosition = nativeReference.scrollTop; // quick upadates to component private member
            this.debouncedScrollUpdater(this);// debounced function to report last update
        });

        $(window).on("resize.scheduler", this.onWindowResize);
    }

    private adjustScroll(scrollPos: number, schedulerElement: JQuery): void {
        let reference = schedulerElement.find("div.k-scheduler-content");
        let nativeReference = reference.get(0);
        let posToScroll = scrollPos;
        if (scrollPos === -1) { // initial load // scroll to current time
            let ht = new Date().getHours();
            let shr = "";

            if (sessionStore.userLanguageCode === "fr") {
                shr = ht + ":00";
            }
            else {
                if (ht === 0)
                    shr = "00:00 AM";
                else if (ht === 12)
                    shr = "12:00 PM";
                else if (ht > 12)
                    shr = (ht - 12) + ":00 PM";
                else if (ht < 12 && ht > 0)
                    shr = ht + ":00 AM";
            }

            schedulerElement.find("table.k-scheduler-table tbody tr th").each(function() {
                let el = $(this);
                let s = el.text();
                if (s === shr) {
                    posToScroll = el.position().top;
                    return false;
                }
            });
        }

        if (nativeReference != null)
            nativeReference.scrollTop = posToScroll;
    }

    // debounced operation that reports scrollposition
    private debouncedScrollUpdater(component: KendoScheduler): void {
        if (component.scrollTimerReference != null) {
            clearTimeout(component.scrollTimerReference);
            component.scrollTimerReference = null;
        }

        component.scrollTimerReference = window.setTimeout(() => {
            let operation = new LocalEvent(LocalOperations.Calendar.OnScroll);
            operation.AddItem("value", component.scrollPosition);
            LocalEventDispatcher.dispatch(operation);
        }, 100);
    }

    public componentWillUnmount(): void {
        this.destroyScheduler();
    }

    public destroyScheduler(): void {
        $(window).off("resize.scheduler");
        let scheduler = $("#" + this.props.ControlId);

        scheduler.off("scroll");
        let activeReference = scheduler.data("kendoScheduler");
        if (activeReference != null) {
            activeReference.wrapper.off("mousedown.cal");
            activeReference.wrapper.off("mousedown.caldiv");
            scheduler.data("kendoScheduler").destroy();
            scheduler.html("");
        }
    }

    public getEmployeesAsKendoDataSource(): any[] {
        const dataSource = [];
        const displayedEmployees = this.props.CalendarModel.get("DisplayedEmployees")?.toArray() ?? [];
        const appointmentDisplay = cacheStore.GetString("AppointmentsMLabel").toLowerCase();
        const availabilitiesDisplay = cacheStore.GetString("AvailabilitiesLabel");

        displayedEmployees.forEach(employee => {
            // hack to get full display of group header, kendo groupHeaderTemplate is 1) undocumented and 2) only accepts two parameters (text + color) and set all data to display in a column separated "text" property
            // it would be proper here to state that telerik really sucks ass
            const nbAppointments = employee.AppointmentCount;
            const nbAvailabilities = employee.AvailabilityCount;
            let csvData = employee.Name + ";" + employee.Title + ";";
            csvData += employee.Specialities.join(" | ") + ";";
            csvData += nbAppointments + ";" + appointmentDisplay + ";" + nbAvailabilities + ";" + availabilitiesDisplay;
            dataSource.push({ text: csvData, value: employee.ContactFacetId });
        });

        return dataSource;
    }

    public onSchedulerGridClick(e: kendo.ui.SchedulerEditEvent): void {
        e.preventDefault();
    }

    public onSchedulerNavigate(date: Date): void {
        sessionStore.dispatch(LocalOperations.Calendar.Calendar_Monthview_DayClick, { date });
    }

    public onViewNavigate(e: kendo.ui.SchedulerNavigateEvent): void {
        e.preventDefault();
        sessionStore.dispatch(LocalOperations.Calendar.Calendar_Monthview_DayClick, { date: e.date });
    }

    private generateAppointmentTemplate(props: IKendoSchedulerProps): string {
        if (props.CalendarModel.get("IsCalendarMonthView"))
            return this.generateAppointmentTemplateMonthView();

        const patient = props.CalendarDisplaySettingsModel.get("PatientsAreVisible");
        const status = props.CalendarDisplaySettingsModel.get("StatusAreVisible");
        const rooms = props.CalendarDisplaySettingsModel.get("RoomsAreVisible");
        const phones = props.CalendarDisplaySettingsModel.get("ContactPointsAreVisible");
        const sites = props.CalendarDisplaySettingsModel.get("SitesAreVisible");
        const nam = props.CalendarDisplaySettingsModel.get("IncludeNAM");
        const oneline = props.CalendarDisplaySettingsModel.get("OneLineViewSelected");
        const isDayViewString = props.CalendarModel.get("IsCalendarDayView") ? "true" : "false";
        const isCanadianTriageAndAcuityScoreVisible = cacheStore.ClinicSettings.EnableCanadianTriageAndAcuityScore && props.CalendarDisplaySettingsModel.get("CanadianTriageAndAcuityScoreVisible");
        const isPatientlessAppointmentsEnabled = cacheStore.ClinicSettings.EnableNewPatientlessAppointmentBehavior;

        // event html template
        let template = `#if (data.hasOwnProperty("HasPatient") && !data.HasPatient && data.AppointmentStatusCode != "CANCELLEDBYPATIENT" && data.AppointmentStatusCode != "CANCELED" && ` + cacheStore.ClinicSettings.EnableNewPatientlessAppointmentBehavior + `) {#
                        <div title="{titleValue}" data-role="cdevent" class="CALENDAREVENT #=SchedulerHelper.GetValueOrEmpty(data.RuleSetExtraClass)# #=SchedulerHelper.GetValueOrEmpty(data.PortalDisplayClass)# event">
                    #} else if (data.AppointmentStatusCode && AppointmentStatusCode =="PREDEFINEDRULESET" && ` + isDayViewString + ` ) {#
                        <div title="{titleValue}" data-role="cdevent" class="#=data.AppointmentStatusCode# #=SchedulerHelper.GetValueOrEmpty(data.RuleSetExtraClass)# #=SchedulerHelper.GetValueOrEmpty(data.PortalDisplayClass)# event ruleset-container">
                    #} else { #
                        <div title="{titleValue}" data-role="cdevent" class="#=data.AppointmentStatusCode# #=SchedulerHelper.GetValueOrEmpty(data.RuleSetExtraClass)# #=SchedulerHelper.GetValueOrEmpty(data.PortalDisplayClass)# event">
                    # } #
                     `;

        // firstline

        let templateFirstLineWithValues = '#=kendo.toString(start, "HH:mm") # - #=kendo.toString(end, "HH:mm") # <strong>#= SchedulerHelper.GetValueOrEmpty(data.IsPaid) #</strong> ';

        templateFirstLineWithValues += this.buildPatientInfo(patient, nam, isCanadianTriageAndAcuityScoreVisible);

        // the hub status label (include a leading ` - ` in oneline mode)
        const hubLabel = oneline
            ? `#if (data.IsQuebecHubBookedAppointment) {# - <strong class="status-#=SchedulerHelper.GetValueOrEmpty(data.QuebecBookingHubDisplayState)#"> HUB </strong>#}#`
            : `#if (data.IsQuebecHubBookedAppointment) {# <strong class="status-#=SchedulerHelper.GetValueOrEmpty(data.QuebecBookingHubDisplayState)#"> HUB </strong>#}#`;

        if (oneline)
            templateFirstLineWithValues += ` ${hubLabel} #if (data.AppointmentStatusCode && AppointmentStatusCode !="PREDEFINEDRULESET" && SchedulerHelper.GetValueOrEmpty(data.AppointmentDescriptionWithoutCombo) != "") {# <strong> - #=SchedulerHelper.GetValueOrEmpty(data.AppointmentDescriptionWithoutCombo)#  <em> #=SchedulerHelper.GetValueOrEmpty(data.ComboName)#  </em> </strong> #}# `;

        templateFirstLineWithValues += ` #if (data.AppointmentStatusCode && AppointmentStatusCode !="PREDEFINEDRULESET") {# <span>#=SchedulerHelper.GetValueOrEmpty(data.PatientIdentifier) #</span> #}# `;
        // order important here

        let templateFirstLine = ` # if (data.AppointmentStatusCode && AppointmentStatusCode !="PREDEFINEDRULESET")
                                      { #
                                            <div class="c-content" iscombo="#=data.ComboId !== undefined && data.ComboId !== null#"> ` + templateFirstLineWithValues.replaceAll("{space}", " ") + this.displayPopulationBasedFundingLine() + this.displayQuestionnaireLine() + ` </div>
                                    # }
                                   else
                                    {
                                       #`;
        if (!this.props.CalendarModel.get("IsCalendarWeekView")) {
            templateFirstLine += `
                                   <section class="ruleset-table">
                                    #
                                        if (data.RuleSetItemsDisplay) {
                                            for (var i=0; i < RuleSetItemsDisplay.length; i++)
                                            {#
                                                <div class="ruleset-row">
                                                    <span style="width:50%" class="ruleset-cell-description">
                                                        <span class="ruleset-cell" title="#= RuleSetItemsDisplay[i].DisplayName #  #= RuleSetItemsDisplay[i].DisplayDuration #">#= RuleSetItemsDisplay[i].DisplayName #  #= RuleSetItemsDisplay[i].DisplayDuration # </span>
                                                    </span>
                                                    <span style="width:30%" class="textcenter">
                                                        <span class="ruleset-cell ">#= RuleSetItemsDisplay[i].DisplayImages # </span>
                                                    </span>
                                                    <span style="width:20%" class="textcenter">
                                                        <span class="ruleset-cell">#= RuleSetItemsDisplay[i].DisplayCounters # </span>
                                                    </span>
                                                </div>
                                            #}
                                       }
                                    #
                                   </section>`;
        }

        templateFirstLine += `
                                    # } #

                                     `;

        // secondline
        let templateSecondLineWithValues = `${hubLabel} #=SchedulerHelper.HtmlEncode(SchedulerHelper.GetValueOrEmpty(data.AppointmentDescription))#`;

        if (rooms)
            templateSecondLineWithValues += "#=SchedulerHelper.GetValueOrEmpty(data.Room)# ";
        if (sites) {
            templateSecondLineWithValues += isPatientlessAppointmentsEnabled
                ? "# if (data.HasPatient) { # #=SchedulerHelper.GetValueOrEmpty(data.SiteName)# # } #"
                : "#=SchedulerHelper.GetValueOrEmpty(data.SiteName)#";
        }

        const templateSecondLineToolTip = cacheStore.ClinicSettings.EnableOnlinePredefinedAppointments
            ? `
                #if(data.IsOnlineBookingAllowed)
                {#
                    {break}
                    ${cacheStore.GetString("AvailableOnPortalLabel").replaceAll(" ", "{space}")}
                #}
                if(SchedulerHelper.GetValueOrEmpty(data.TargetedClientName) !== "")
                {#
                    {break}
                    ${cacheStore.GetString("ClienteleLabel")}: {space} #=SchedulerHelper.GetValueOrEmpty(data.TargetedClientName)#
                #}#
            `
            : "";

        let templateSecondLine = ` #if (data.AppointmentStatusCode && AppointmentStatusCode != "PREDEFINEDRULESET")
            {#
                <div class="c-subject">` + templateSecondLineWithValues + `</div>#
            }#`;

        // thirdline
        let templateThirdLine = "";
        let templateThirdLineWithValues = "";
        if (status) {
            templateThirdLine += isPatientlessAppointmentsEnabled
                ? "# if (data.HasPatient) { # <div> #=SchedulerHelper.GetValueOrEmpty(data.AppointmentStatus)#</div> # } #"
                : "<div> #=SchedulerHelper.GetValueOrEmpty(data.AppointmentStatus)#</div>";

            templateThirdLineWithValues += isPatientlessAppointmentsEnabled
                ? "# if (data.HasPatient) { # #=SchedulerHelper.GetValueOrEmpty(data.AppointmentStatus)#{break} # } #"
                : "#=SchedulerHelper.GetValueOrEmpty(data.AppointmentStatus)#{break}";
        }
        if (phones) {
            templateThirdLine += '<div> #=SchedulerHelper.GetValueOrEmpty( (data.ContactPoints && data.ContactPoints.length>0) ? data.ContactPoints[0] : "")#</div>';
            templateThirdLineWithValues += '#=SchedulerHelper.GetValueOrEmpty( (data.ContactPoints && data.ContactPoints.length>0) ? data.ContactPoints[0] : "")#';
        }

        template += templateFirstLine;

        let tooltip = `${templateFirstLineWithValues} {break} ${templateSecondLineWithValues.trim()} {space} ${templateSecondLineToolTip} {break} ${templateThirdLineWithValues}`;
        if (!oneline) {
            template += templateSecondLine;
            template += templateThirdLine;
        }

        const htmlTagRegex = /<[^>]+>/g;
        tooltip = tooltip.replace(htmlTagRegex, "").split(" ").join("").split("\n").join(" ").replaceAll("{break}", "\n").replaceAll("{space}", " ");

        template += "</div>";
        template = template.replaceAll("{titleValue}", tooltip);

        return template;
    }

    private generateAppointmentTemplateMonthView(): string {
        let template: string;
        template = '<div data-role="cdevent" class="#=data.AppointmentStatusCode# event">';

        template += `
                                # if (data.AppointmentStatusCode =="RULESETAVAILABILITY")
                                      { #
                                            #=SchedulerHelper.GetValueOrEmpty(data.MaxRulesetAvailability)##=SchedulerHelper.GetValueConditional(" min", data.MaxRulesetAvailability > 0)#
                                    # }
                                   else
                                    {
                                       #
                                          <strong>
                                          #=SchedulerHelper.GetValueOrZero(data.NbAppointments)#
                                          #=SchedulerHelper.GetValueConditional(" | ", data.NbAvailabilities > 0) #
                                          </strong >
                                    # }#

                    # if (data.AppointmentStatusCode !="RULESETAVAILABILITY")
                        { #
                            <span class="SmallText">
                                        #=SchedulerHelper.GetValueConditional(SchedulerHelper.GetValueOrZero(data.NbAvailabilities), data.NbAvailabilities>0)#
                            </span>

                        # } #
                    `;

        template += "</div>";

        return template;
    }

    private getKendoElements(): KendoElement[] {
        return $(".k-event").toArray().map(p => new KendoElement(p));
    }

    // properly layout rulesets in calendar day view(ie. always at left of swimlane)
    public layoutRulesets(): void {
        let self = this;

        // get amount of swimlanes displayed (nb employee x weekdays)
        let firstSwimlaneElements = $(".k-scheduler-content").find(".k-middle-row").first().children();
        let nbSwimLanes = firstSwimlaneElements.length;

        // first get the left coordinates of swimlines
        let leftx_swimlanes: number[] = new Array(nbSwimLanes);
        for (var i = 0; i < nbSwimLanes; i++) {
            leftx_swimlanes[i] = $(firstSwimlaneElements[i]).offset().left;
        }

        const allElements = this.getKendoElements();

        const eventsBySwimlane = allElements.filter(p => !p.IsRuleset).toLookupDictionary(p => this.getElementSwimlane(leftx_swimlanes, p).toString());
        const rulesetsBySwimlane = allElements.filter(p => p.IsRuleset).toLookupDictionary(p => this.getElementSwimlane(leftx_swimlanes, p).toString());

        for (var i = 0; i < nbSwimLanes; i++) {
            this.arrangeSwimLaneEvents(eventsBySwimlane.get(i), rulesetsBySwimlane.get(i));
        }

        // render ruleset collapsed or not, and adjust event's width accordingly
        let newRulesetWidth = null;
        if (this.props.CalendarDisplaySettingsModel.get("AreRulesetsCollapsed"))
            newRulesetWidth = 15;
        if (this.props.CalendarModel.get("IsCalendarWeekView"))
            newRulesetWidth = 5;
        for (var i = 0; i < nbSwimLanes; i++) {
            this.maximizeElementsWitdthInSwimlane(eventsBySwimlane.get(i), rulesetsBySwimlane.get(i), newRulesetWidth);
        }

        // highlight timeslots that are at same time as ruleset
        let allSlots = $(".k-scheduler-content").find("td").toArray();
        $(allSlots).removeClass("k-nonwork-hour");

        for (let predefinedApt of allSlots.map(p => new KendoElement(p))) {
            const swimlaneNumber = self.getElementSwimlane(leftx_swimlanes, predefinedApt);

            if (this.isEventInAnyRuleSet(rulesetsBySwimlane.get(swimlaneNumber), predefinedApt))
                predefinedApt.Ref.css("background-color", "#f5f7f9");
        }

        $("tr.ruleset-row > td").css("background-color", "#ffffff"); // ensure ruleset descriptions bg is white
    }

    // check if an element is located in the vertical coordinates of any ruleset in a list
    private isEventInAnyRuleSet(swimlaneRuleSets: KendoElement[], event: KendoElement): boolean {
        return (swimlaneRuleSets || [])
            .any(ruleset => this.areElementsIntersecting(ruleset, event));
    }

    // rearrange swimlane elements so that the rulesets are always leftmost
    private arrangeSwimLaneEvents(swimlaneEvents: KendoElement[], swimlaneRulesets: KendoElement[]): void {
        if (swimlaneEvents == null || swimlaneRulesets == null)
            return;

        // get left bound of current swim lame by extrapolating from smallest possible values
        let swimlaneSmallestLeftPos = 9999999999;
        for (let ruleset of swimlaneRulesets) {
            if (ruleset.Offset.left < swimlaneSmallestLeftPos)
                swimlaneSmallestLeftPos = ruleset.Offset.left;
        }
        for (let event of swimlaneEvents) {
            if (event.Offset.left < swimlaneSmallestLeftPos)
                swimlaneSmallestLeftPos = event.Offset.left;
        }

        // set rulesets to leftmost of swimlane
        let rulesetStartLeftPos = swimlaneSmallestLeftPos;
        for (let ruleset of swimlaneRulesets) {
            // nothing to change
            if (ruleset.Offset.left == rulesetStartLeftPos)
                continue;

            // flip left positions of events that are now at same left pos than the ruleset
            const elements = swimlaneEvents
                .filter(element =>
                    this.areElementsIntersecting(ruleset, element)
                    // is in same column
                    && element.Offset.left * 0.99 <= rulesetStartLeftPos
                    && rulesetStartLeftPos <= element.Offset.left * 1.01
                );
            for (let element of elements) {
                const newOffSet: JQueryCoordinates = { top: element.Offset.top, left: ruleset.Offset.left };
                element.Ref.offset(newOffSet);
                element.Offset = newOffSet;
            }

            // bring back ruleset to most left slot
            const newOffSetRuleset: JQueryCoordinates = { top: ruleset.Offset.top, left: rulesetStartLeftPos };
            ruleset.Ref.offset(newOffSetRuleset);
            ruleset.Offset = newOffSetRuleset;
        }
    }

    // adjusts the width of elements in a swimlane (ie shrink a ruleset to thin band, or maximize appointment width when double booked)
    private maximizeElementsWitdthInSwimlane(swimlaneEvents: KendoElement[] = [], swimlaneRulesets: KendoElement[] = [], newRulesetWidth: number = null): void {
        for (let event of swimlaneEvents)
            event.WidthAdjusted = false;

        for (let ruleset of swimlaneRulesets) {
            // get events that intersect with a ruleset
            const intersectingEvents = swimlaneEvents
                .filter(p =>
                    p.WidthAdjusted == false
                    && this.areElementsIntersecting(ruleset, p)
                );

            // sort these events by which absolute left pos (ie column in swinlane) they are
            const eventsByLeftPos = this.getSwimlaneEventsByColumns(intersectingEvents);

            const desiredRuleSetWidth = newRulesetWidth ?? ruleset.Ref.width() ?? 0;

            // get width information
            const rulesetOffset = ruleset.Offset;

            // get amount of columns events are displayed in the swimlane
            const nbColumns = eventsByLeftPos.length;
            if (nbColumns == 0)
                continue;

            for (let event of intersectingEvents)
                event.WidthAdjusted = true;

            const firstColumnEvents = eventsByLeftPos[0];
            const currentSwimlaneWidth = (firstColumnEvents[0].Ref.width() * (nbColumns + 1)); // add 1 to take uncollapsed (yet) ruleset into account
            const eventColumnsWidth = currentSwimlaneWidth - desiredRuleSetWidth - 5; // leave 15 px for rulesetband + 5px padding
            const newEventWidth = (eventColumnsWidth - (nbColumns * 2)) / nbColumns; // leave 2px padding between events

            // resize ruleset and tweak appearance if we are collapsing it
            if (newRulesetWidth != null) {
                ruleset.Ref.width(desiredRuleSetWidth);
                ruleset.Ref.addClass("pointer");
                ruleset.Ref.find(".ruleset-table, .k-event-bottom-actions, .k-event-top-actions, .k-event-actions").remove();
            }

            let newLeftPos = (rulesetOffset.left + 5) + desiredRuleSetWidth;

            // resize and change event position
            for (let eventIndex = 0; eventIndex < eventsByLeftPos.length; eventIndex++) {
                const columnEvents = eventsByLeftPos[eventIndex];

                for (let columnEvent of columnEvents) {
                    let eventOffset = columnEvent.Offset;
                    // check if columns at right of element are empty, if so stretch
                    let rightStretch = this.getNbColumnsEventCanBeStretchedIn(columnEvent, eventIndex, eventsByLeftPos);
                    columnEvent.Ref.width(newEventWidth * (rightStretch + 1));

                    let newOffSet: JQueryCoordinates = { top: eventOffset.top, left: newLeftPos };
                    columnEvent.Ref.offset(newOffSet);
                    columnEvent.Offset = newOffSet;
                }

                newLeftPos = newLeftPos + newEventWidth + 5;
            }
        }
    }

    // returns the number of the swimlane that contains an element
    private getElementSwimlane(swimlanesLeftPositions: number[], elem: KendoElement): number {
        // assume we have one swimlane if 0-1 are defined
        if (!swimlanesLeftPositions || swimlanesLeftPositions.length <= 1)
            return 0;

        const lastIndex = swimlanesLeftPositions.length - 1;
        for (let i = 0; i < lastIndex; i++) {
            let nextSwimlaneLeft = swimlanesLeftPositions[i + 1];
            if (elem.Offset.left < nextSwimlaneLeft)
                return i;
        }

        // far right, is on last swimlane
        return lastIndex;
    }

    // checks if an element is in same vertical plane as another element
    private areElementsIntersecting(ruleset: KendoElement, element: KendoElement): boolean {
        const rulesetBottom = ruleset.Offset.top + ruleset.Height;
        const elemBottom = element.Offset.top + element.Height;

        return elemBottom > ruleset.Offset.top && element.Offset.top < rulesetBottom;
    }

    // groups element by columns (where key is left offset coordinate)
    private getSwimlaneEventsByColumns(swimlaneEvents: KendoElement[]): KendoElement[][] {
        let dicResult: { [leftOffset: number]: KendoElement[] } = {};
        let keys: string[] = [];

        for (let event of swimlaneEvents) {
            const offset = event.Offset.left;

            if (!dicResult[offset]) {
                dicResult[offset] = [];
                keys.push(offset.toString());
            }
            dicResult[offset].push(event);
        }

        return keys.map(key => dicResult[key]);
    }

    // checks is an event can be stretched in its right-hand columns, and for how many columns its width can be stretched
    private getNbColumnsEventCanBeStretchedIn(event: KendoElement, eventsColumnIdx: number, eventColumns: KendoElement[][]): number {
        let result = 0;

        if (eventColumns == null || eventColumns.length < eventsColumnIdx || event == null)
            return result;

        let nbColumnsFreeRight = 0;

        let self = this;

        // check on columns to right
        for (let i = eventsColumnIdx + 1; i < eventColumns.length; i++) {
            let eventsInColumn = eventColumns[i];
            let isNextColumnFree = eventsInColumn
                .every(x => self.areElementsIntersecting(event, x) == false);
            if (isNextColumnFree) nbColumnsFreeRight++;
        }

        return nbColumnsFreeRight;
    }

    private get redirectPatientClass(): string {
        return "redirect-patient";
    }

    private get questionnaireClickClass(): string {
        return "questionnaire-click";
    }

    private buildPatientInfo(showName: boolean, showHealthCardNumber: boolean, showTriageScore: boolean): string {
        if (!showName && !showHealthCardNumber && !showTriageScore)
            return "";

        const name = showName ? "#=SchedulerHelper.GetValueOrEmpty(data.PatientName)#" : "";
        const healthCardNumber = showHealthCardNumber ? "#=SchedulerHelper.GetValueOrEmpty(data.PatientNAM)#" : "";

        return `
            #if (data.AppointmentStatusCode && AppointmentStatusCode !="PREDEFINEDRULESET") {#
                <strong>
                    #if (data.PatientContactId) {#
                        <a class="${this.redirectPatientClass}" > ${name}{space}${healthCardNumber}</a>
                    #} else {#
                        <span> ${name}{space}${healthCardNumber}</span>
                    #}#
                    #if (${showTriageScore}) {#
                        <span>${cacheStore.GetString("TriageLabel")}: #:SchedulerHelper.GetValueOrEmpty(data.CanadianTriageAndAcuityScore)#</span>
                    #}#
                </strong>
            #}#
        `;
    }

    private displayQuestionnaireLine(): string {
        return `
            #if (data.AppointmentStatusCode && AppointmentStatusCode !="PREDEFINEDRULESET" && data.QuestionnaireStatusInfo) {#
                <span>
                    #if (data.QuestionnaireStatusInfo.Result === true) {#
                        <span class="covid-positive">
                            <a class="${this.questionnaireClickClass}"> #=cacheStore.getQuestionnaireShortName(data.QuestionnaireStatusInfo.QuestionnaireId)# <span style="vertical-align: text-top;" class="glyphicon glyphicon-remove"></span></a>
                        </span>
                    #} else if (data.QuestionnaireStatusInfo.Result === false) {#
                        <span class="covid-negative">
                            <a class="${this.questionnaireClickClass}"> #=cacheStore.getQuestionnaireShortName(data.QuestionnaireStatusInfo.QuestionnaireId)# <span style="vertical-align: text-top;" class="glyphicon glyphicon-ok"></span></a>
                        </span>
                    #} else {#
                        <span class="covid-noresult">
                            #if (QuestionnaireLinkConfiguration.shouldLinkBeActive(data.start, data.AppointmentStatusLookupId, false, data.PatientId, data.IsSharedWithPortal, data.QuestionnaireStatusInfo.QuestionnaireInstanceId)) {#
                                <a class="${this.questionnaireClickClass}"> #=cacheStore.getQuestionnaireShortName(data.QuestionnaireStatusInfo.QuestionnaireId)# <span>&Oslash;</span></a>
                            #} else {#
                                <span class="not-allowed-hover"> #=cacheStore.getQuestionnaireShortName(data.QuestionnaireStatusInfo.QuestionnaireId)# <span>&Oslash;</span></span>
                            #}#
                        </span>
                    #}#
                </span>
            #}#
        `;
    }

    private handleOnQuestionnaireClick(questionnaireStatusInfo: QuestionnaireStatusInfo): void {
        sessionStore.dispatch(
            LocalOperations.Calendar.ShowQuestionnaire,
            {
                questionnaireStatusInfo,
                showQuestionnaire: true
            }
        );
    }

    private displayPopulationBasedFundingLine(): string {
        if (!cacheStore.ClinicSettings.EnablePopulationBasedFunding)
            return "";

        return `
                #if (data.AppointmentStatusCode && AppointmentStatusCode !="PREDEFINEDRULESET") {#
                    #if (data.PatientContactId) {#
                        <div class="pbf-icon-container">
                            <div class="pbf-icon #:SchedulerHelper.getPopulationBasedFundingStatusIcon(data)#"></div>
                            <span class="pbf-physician">#:SchedulerHelper.getPopulationBasedFundingPratictionerName(data)#</span>
                        </div>
                    #}#
                #}#
        `;
    }
}

class KendoElement {
    public Ref: JQuery;
    public Offset: JQueryCoordinates;
    public Height: number;
    public WidthAdjusted: boolean = false;
    public IsRuleset: boolean = false;

    constructor(ref: Element) {
        this.Ref = $(ref);
        this.Offset = this.Ref.offset();
        this.Height = this.Ref.height();
        this.IsRuleset = this.Ref.find(".PREDEFINEDRULESET").length > 0;
    }
}

export { KendoScheduler };
